<template>
    <div class="min-h-screen">
        <Banner :title="t('technology.title')" :subtitle="t('technology.subtitle')" />

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('technology.innovation.title') }}</h2>
                    <p class="text-[#8d6e63] mb-8">{{ t('technology.innovation.subtitle') }}</p>
                </div>
                <div class="max-w-3xl mx-auto">
                    <div class="space-y-8">
                        <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.innovation.management.title') }}</h3>
                            <p class="text-[#212121] mb-6 leading-relaxed">{{ t('technology.innovation.management.content') }}</p>
                        </div>
                        <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.innovation.team.title') }}</h3>
                            <p class="text-[#212121] mb-6 leading-relaxed">{{ t('technology.innovation.team.content') }}</p>
                        </div>
                        <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.innovation.results.title') }}</h3>
                            <p class="text-[#212121] mb-6 leading-relaxed">{{ t('technology.innovation.results.content') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('technology.core.title') }}</h2>
                    <p class="text-[#8d6e63] mb-8">{{ t('technology.core.subtitle') }}</p>
                </div>
                <div class="max-w-4xl mx-auto">
                    <ul class="flex justify-center border-b-2 border-gray-200 mb-8">
                        <li v-for="tab in tabs" 
                            :key="tab.id"
                            :class="[
                                'px-8 py-4 cursor-pointer transition-all duration-300 relative',
                                currentTab === tab.id ? 'text-[#005005] font-bold' : 'text-[#8d6e63]'
                            ]"
                            @click="currentTab = tab.id">
                            {{ tab.name }}
                            <div v-if="currentTab === tab.id" class="absolute bottom-0 left-0 w-full h-0.5 bg-[#005005]"></div>
                        </li>
                    </ul>
                    
                    <div class="mt-8">
                        <div v-if="currentTab && tabs.find(t => t.id === currentTab)" class="max-w-3xl mx-auto">
                            <img :src="tabs.find(t => t.id === currentTab)?.image" 
                                 :alt="tabs.find(t => t.id === currentTab)?.name" 
                                 class="w-full h-auto rounded-lg shadow-lg mb-8">
                            <div class="space-y-8">
                                <template v-for="(_, index) in tabs.find(t => t.id === currentTab)?.details" :key="index">
                                    <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                                        <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t(`technology.core.items.${currentTab}.details.${index}.title`) }}</h3>
                                        <p class="text-[#212121] mb-6 leading-relaxed">{{ t(`technology.core.items.${currentTab}.details.${index}.content`) }}</p>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('technology.research.title') }}</h2>
                    <p class="text-[#8d6e63] mb-8">{{ t('technology.research.subtitle') }}</p>
                </div>
                <div class="max-w-3xl mx-auto">
                    <img src="/images/technology/YanFaZhongXinSuoLve.png" :alt="t('technology.research.imageAlt')" class="w-full h-auto rounded-lg shadow-lg mb-8">
                    <div class="space-y-8">
                        <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.research.center.title') }}</h3>
                            <p class="text-[#212121] mb-6 leading-relaxed">{{ t('technology.research.center.content') }}</p>
                        </div>
                        <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                            <h4 class="text-lg font-bold text-[#005005] mb-4">{{ t('technology.research.cooperation.title') }}</h4>
                            <p class="text-[#212121] mb-6 leading-relaxed">{{ t('technology.research.cooperation.content') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('technology.patents.title') }}</h2>
                    <p class="text-[#8d6e63] mb-8">{{ t('technology.patents.subtitle') }}</p>
                </div>
                <div class="max-w-4xl mx-auto space-y-8">
                    <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-start gap-8">
                        <div class="text-4xl text-[#005005] flex-shrink-0">
                            <i class="fas fa-lightbulb"></i>
                        </div>
                        <div class="flex-1">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.patents.authorized.title') }}</h3>
                            <ul class="space-y-2">
                                <li v-for="(_, index) in 4" :key="index" class="text-[#212121] pl-6 relative">
                                    <span class="absolute left-0 text-[#005005]">•</span>
                                    {{ t(`technology.patents.authorized.items.${index}`) }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-start gap-8">
                        <div class="text-4xl text-[#005005] flex-shrink-0">
                            <i class="fas fa-cogs"></i>
                        </div>
                        <div class="flex-1">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.patents.projects.title') }}</h3>
                            <ul class="space-y-2">
                                <li v-for="(_, index) in 5" :key="index" class="text-[#212121] pl-6 relative">
                                    <span class="absolute left-0 text-[#005005]">•</span>
                                    {{ t(`technology.patents.projects.items.${index}`) }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-start gap-8">
                        <div class="text-4xl text-[#005005] flex-shrink-0">
                            <i class="fas fa-trophy"></i>
                        </div>
                        <div class="flex-1">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.patents.awards.title') }}</h3>
                            <ul class="space-y-2">
                                <li class="text-[#212121] pl-6 relative">
                                    <span class="absolute left-0 text-[#005005]">•</span>
                                    {{ t('technology.patents.awards.items.0') }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="bg-white p-8 rounded-lg shadow-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-start gap-8">
                        <div class="text-4xl text-[#005005] flex-shrink-0">
                            <i class="fas fa-book"></i>
                        </div>
                        <div class="flex-1">
                            <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('technology.patents.papers.title') }}</h3>
                            <ul class="space-y-2">
                                <li v-for="(_, index) in 5" :key="index" class="text-[#212121] pl-6 relative">
                                    <span class="absolute left-0 text-[#005005]">•</span>
                                    {{ t(`technology.patents.papers.items.${index}`) }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="py-20 bg-gray-50">
            <div class="container mx-auto px-4">
                <div class="text-center mb-12">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] inline-block pb-2">{{ t('technology.projects.title') }}</h2>
                    <p class="text-[#8d6e63] mb-8">{{ t('technology.projects.subtitle') }}</p>
                </div>
                <div class="max-w-3xl mx-auto">
                    <div class="space-y-12">
                        <div v-for="(_, index) in 6" :key="index" class="flex">
                            <div class="w-8 mr-8 relative">
                                <div class="w-5 h-5 bg-[#005005] rounded-full mt-2"></div>
                                <div v-if="index < 5" class="absolute top-7 left-2.5 w-0.5 h-full bg-[#005005]"></div>
                            </div>
                            <div class="flex-1 bg-white p-8 rounded-lg shadow-md" :class="{'border-l-4 border-[#005005]': index < 2}">
                                <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t(`technology.projects.items.${index}.title`) }}</h3>
                                <p class="text-[#212121] text-sm mb-2">{{ t(`technology.projects.items.${index}.period`) }}</p>
                                <p class="text-[#005005] font-bold mb-2">{{ t(`technology.projects.items.${index}.cooperation`) }}</p>
                                <p class="text-[#212121] leading-relaxed">{{ t(`technology.projects.items.${index}.desc`) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <CTA
          :title="t('technology.cta.title')"
          :description="t('technology.cta.subtitle')"
          :buttonText="t('technology.cta.button')"
          buttonLink="/contact"
          backgroundImage="/images/56.png"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Banner from '../components/Banner.vue'
import CTA from '../components/CTA.vue'

const { t } = useI18n()
const currentTab = ref('pulping')

onMounted(() => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    })
})

const tabs = ref([
    {
        id: 'pulping',
        name: t('technology.core.tabs.pulping'),
        image: '/images/technology/ZhiJiangSuoLve.png',
        details: [
            {
                title: t('technology.core.items.pulping.details.0.title'),
                content: t('technology.core.items.pulping.details.0.content')
            },
            {
                title: t('technology.core.items.pulping.details.1.title'),
                content: t('technology.core.items.pulping.details.1.content')
            }
        ]
    },
    {
        id: 'bleaching',
        name: t('technology.core.tabs.bleaching'),
        image: '/images/technology/XunHuanSuoLve.png',
        details: [
            {
                title: t('technology.core.items.bleaching.details.0.title'),
                content: t('technology.core.items.bleaching.details.0.content')
            }
        ]
    },
    {
        id: 'papermaking',
        name: t('technology.core.tabs.papermaking'),
        image: '/images/technology/ChuangXinJiShuSuoLve.png',
        details: [
            {
                title: t('technology.core.items.papermaking.details.0.title'),
                content: t('technology.core.items.papermaking.details.0.content')
            },
            {
                title: t('technology.core.items.papermaking.details.1.title'),
                content: t('technology.core.items.papermaking.details.1.content')
            }
        ]
    }
])
</script>

<style scoped>
/* 移除所有自定义CSS，因为已经使用Tailwind类替代 */
</style> 